/**!
 * Created by Leonardo Cheslacki on 05/06/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import PageController from './PageController';

import {HelpersIcon} from './../helpers';

import config from '../../../../config';

const {default: {init: {frame}, view, button}} = config;

import WithType from './../helpers/WithType';

let _this = {};

/**
 *
 */
class PageCurrent extends PageController {
    /**
     *
     * @param props
     * @param main
     */
    constructor(props, main) {
        super(props);
        this.props = props;
        this.main = main;

        this.state = {};

        this.$helpersIcon = HelpersIcon(this)({current_name: 'page'});

        _this = this;
    };

    $functions = {
        /**
         *
         * @param event
         */
        handle: function (event) {

        }
    };

    /**
     * Not usage direct.
     * @param execute
     */
    defaultInit = (execute = null) => {
        const {current_name} = this.props;

        this.main.$current_name = current_name;

        this.gotoAndStopFrame(this.get(), frame);

        this.pageIn();

        this.$helpersIcon.setIcon(this.get(), button.color_icon);

        (typeof (execute) === 'function') && execute();

        /**
         * Preloader
         */
        this.main.emit('process_end', {}, current_name);
    };

    /**
     * Not usage direct.
     * @param execute
     * @param dynamic
     * @param current_page
     */
    defaultPaginateInit = (execute = null, dynamic = false, current_page = null) => {
        const
            {current_name} = this.props,
            stagePaginate = this.main.$stagePaginate({current_name: current_name, current_page: current_page});

        this.main.$current_name = current_name;

        this.gotoAndStopFrame(this.get(), frame);

        this.pageIn();

        stagePaginate.initPaginate(dynamic);

        this.$helpersIcon.setIcon(this.get(), button.color_icon);

        (typeof (execute) === 'function') && execute(stagePaginate);

        /**
         * Preloader
         */
        this.main.emit('process_end', {}, current_name);
    };

    /**
     * Single page.
     * Included helpersIcon color.
     */
    initCover = this.defaultInit;

    /**
     * Single page.
     * Included helpersIcon color.
     */
    initGallery = this.defaultInit;

    /**
     * Single page.
     * Included helpersIcon color.
     * For all pages simple.
     */
    initRecord = this.defaultInit;

    /**
     * Paginate.
     * Included helpersIcon color.
     * For all pages simple.
     */
    initPaginate = this.defaultPaginateInit;

    /**
     * Paginate.
     * Included helpersIcon color.
     */
    initLocalization = this.defaultPaginateInit;

    /**
     * Paginate.
     * Included helpersIcon color.
     */
    initDifferential = this.defaultPaginateInit;

    /**
     * Single page.
     * Included stagePin and helpersIcon color.
     */
    initImplantation = () => {
        this.defaultInit(() => {
            const {current_name} = this.props;
            this.main.$stagePin({current_name: current_name}).initPin();
        });
    };

    /**
     * Single page.
     * Included stageSun and helpersIcon color.
     */
    initOrientation = () => {
        this.defaultInit(() => {
            const
                _this_frame = this.getFrame(),
                {current_name} = this.props;

            this.$helpersIcon.setIcon(_this_frame, button.color_icon);

            this.main.$stageSun({current_name: current_name}).initSun();
        });
    };

    /**
     * Paginate.
     * Included stagePaginate, layerDifferential, layerCamera, layerPin and helpersIcon color.
     * @param current_page
     */
    initPlants = (current_page) => {
        this.defaultPaginateInit((stagePaginate) => {
            const
                _this_current = this.get(),
                {current_name} = this.props,
                paginates = this.getObject(this.getCurrentIndex(), 'paginates'),
                paginate = this.getCurrentIndex(paginates) || this.getDefaultIndex(paginates),
                active_differential = this.verifyField(paginate, 'active.differential'),
                active_differential_enabled = this.verifyField(paginate, 'active.differential_enabled'),
                active_camera = this.verifyField(paginate, 'active.camera'),
                active_pin = this.verifyField(paginate, 'active.pin');

            if (active_differential) {
                const layerDifferential = this.main.$layerDifferential({
                    current_name: current_name,
                    current_page: current_page,
                    enabled: active_differential_enabled
                });
                layerDifferential.initDifferential();
                stagePaginate.setState({...stagePaginate.state, differential: layerDifferential});
            }

            if (active_camera) {
                const layerCamera = this.main.$layerCamera({current_name: current_name, current_page: current_page});
                layerCamera.initCamera();
                stagePaginate.setState({...stagePaginate.state, camera: layerCamera});
            }

            if (active_pin) {
                const layerPin = this.main.$layerPin({current_name: current_name, current_page: current_page});
                layerPin.initPin();
                stagePaginate.setState({...stagePaginate.state, pin: layerPin});
            }

            for (let property of paginates) {
                this.$helpersIcon.setIcon(_this_current, button.color_icon, this.toNumber(property.name));
            }

            this.$helpersIcon.disabledAnimate(_this_current)(this.toNumber(current_page));
        }, false, current_page);
    };

    /**
     * Paginate.
     * Included stagePaginate, stageView and helpersIcon color.
     */
    initViews = () => {
        this.defaultPaginateInit(() => {
            const {current_name} = this.props;
            this.main.$stageView({current_name: current_name}).initView();
        }, true);
    };

    /**
     * Single page.
     * Included helpersIcon color.
     */
    initProject = () => {
        this.defaultInit(() => {
            const {occ_project: {occ_project_01, occ_project_02, occ_project_03}} = this.getFrame();

            this._handleProjectMore01 && occ_project_01.btn_more.off('mousedown', this._handleProjectMore01);
            this._handleProjectMore01 = occ_project_01.btn_more.on('mousedown', () => {
                (occ_project_02.currentLabel === 'complete') && occ_project_02.gotoAndPlay('complete');
                (occ_project_03.currentLabel === 'complete') && occ_project_03.gotoAndPlay('complete');

                occ_project_01.gotoAndPlay('start');
            });

            this._handleProjectLess01 && occ_project_01.btn_less.off('mousedown', this._handleProjectLess01);
            this._handleProjectLess01 = occ_project_01.btn_less.on('mousedown', () => {
                occ_project_01.gotoAndPlay('complete');
            });

            this.$helpersIcon.setIcon(occ_project_01, button.color_icon);
            this.$helpersIcon.setIcon(occ_project_01.btn_more, button.color_icon);

            this._handleProjectMore02 && occ_project_02.btn_more.off('mousedown', this._handleProjectMore02);
            this._handleProjectMore02 = occ_project_02.btn_more.on('mousedown', () => {
                (occ_project_01.currentLabel === 'complete') && occ_project_01.gotoAndPlay('complete');
                (occ_project_03.currentLabel === 'complete') && occ_project_03.gotoAndPlay('complete');

                occ_project_02.gotoAndPlay('start');
            });

            this._handleProjectLess02 && occ_project_02.btn_less.off('mousedown', this._handleProjectLess02);
            this._handleProjectLess02 = occ_project_02.btn_less.on('mousedown', () => {
                occ_project_02.gotoAndPlay('complete');
            });

            this.$helpersIcon.setIcon(occ_project_02, button.color_icon);
            this.$helpersIcon.setIcon(occ_project_02.btn_more, button.color_icon);

            this._handleProjectMore03 && occ_project_03.btn_more.off('mousedown', this._handleProjectMore03);
            this._handleProjectMore03 = occ_project_03.btn_more.on('mousedown', () => {
                (occ_project_01.currentLabel === 'complete') && occ_project_01.gotoAndPlay('complete');
                (occ_project_02.currentLabel === 'complete') && occ_project_02.gotoAndPlay('complete');

                occ_project_03.gotoAndPlay('start');
            });

            this._handleProjectLess03 && occ_project_03.btn_less.off('mousedown', this._handleProjectLess03);
            this._handleProjectLess03 = occ_project_03.btn_less.on('mousedown', () => {
                occ_project_03.gotoAndPlay('complete');
            });

            this.$helpersIcon.setIcon(occ_project_03, button.color_icon);
            this.$helpersIcon.setIcon(occ_project_03.btn_more, button.color_icon);
        });
    };

    /**
     * Paginate.
     * Included stagePaginate and helpersIcon color.
     */
    initSlide = () => {
        this.defaultPaginateInit(() => {
            const
                _this_current = this.get(),
                occ_pseudo_frame = this.verifyField(_this_current, 'occ_pseudo_frame01'),
                btn_frame = this.verifyField(_this_current, 'btn_frame01');

            this.$helpersIcon.disabledAnimate(_this_current)(1, 'frame');

            this.$helpersIcon.setIcon(_this_current, button.color_icon, 1);

            this.$helpersIcon.setIcon(_this_current, button.color_icon, 2);

            this.$helpersIcon.setIcon(_this_current, button.color_icon, 3);

            occ_pseudo_frame && (occ_pseudo_frame.visible = true);

            btn_frame && (btn_frame.visible = false);
        }, true);
    };

    /**
     * Single page.
     * Included stageZoom.
     */
    initPlant = () => {
        this.defaultInit(() => {
            const {current_name} = this.props;
            this.main.$stageZoom({current_name: current_name}).initZoom();
        });
    };

    /**
     * Single page.
     * Included stageSlider.
     */
    initSlider = () => {
        this.defaultInit(() => {
            const {current_name} = this.props;
            this.main.$stageSlider({current_name: current_name}).initSlider();
        });
    };

    /**
     * Single page.
     * Included stageScroll.
     */
    initScroll = () => {
        this.defaultInit(() => {
            const {current_name} = this.props;
            this.main.$stageScroll({current_name: current_name}).initPin();
        });
    };

    /**
     * Single page.
     * Included stageQuiz.
     */
    initVertical = () => {
        this.defaultInit(() => {
            const {current_name} = this.props;
            this.main.$stageQuiz({current_name: current_name}).initQuiz();
        });
    };

    /**
     *
     * @param current_page
     */
    initDeployments = (current_page) => {
        this.defaultPaginateInit((stagePaginate) => {
            const
                _this_current = this.get(),
                {current_name} = this.props,
                paginates = this.getObject(this.getCurrentIndex(), 'paginates');

            let tintColor = button.color_icon;

            if (this.inArray(['rededor01', 'rededor02', 'rededor03'], current_page)) {
                tintColor = '0xFFFFFF';
            }

            if (!this.inArray(['rededor01', 'rededor03'], current_page)) {
                const
                    stagePin = this.main.$stagePin({current_name: current_name}),
                    paginate = this.getCurrentIndex(paginates) || this.getDefaultIndex(paginates);

                stagePin.props.xml = this.verifyField(paginate, 'xml');
                stagePin.props.content_text = this.verifyField(paginate, 'content_text');

                stagePin.initPin();
            }

            for (let property of paginates) {
                this.$helpersIcon.setIcon(_this_current, tintColor, this.toNumber(property.name));
            }

            this.$helpersIcon.disabledAnimate(_this_current)(this.toNumber(current_page));
        });
    };

    /**
     *
     * @param current_page
     * @param page
     */
    initDeploymentsAndOrientation = (current_page, page) => {
        const
            _this_current = this.get(),
            {current_name} = this.props,
            stagePaginate = this.main.$stagePaginate({current_name: current_name, current_page: current_page});

        this.main.$current_name = current_name;

        this.gotoAndStopFrame(_this_current, frame);

        this.$helpersIcon.disabledAnimate(_this_current)(this.toNumber(page));

        this.pageIn();

        if (page === 1) {
            const
                stagePin = this.main.$stagePin({current_name: current_name}),
                object = this.getCurrentIndex(),
                paginates = this.getObject(object, 'paginates'),
                paginate = this.getCurrentIndex(paginates) || this.getDefaultIndex(paginates);

            stagePin.props.xml = this.verifyField(paginate, 'xml');
            stagePin.props.content_text = this.verifyField(paginate, 'content_text');

            this.destroyTicker('_handleZoom');

            stagePin.initPin();
        } else if (page === 2) {
            const stageSun = this.main.$stageSun({current_name: current_name});

            this.destroyTicker('_handleZoom');

            stageSun.initSun();
        } else if (page === 3) {
            const stageZoom = this.main.$stageZoom({current_name: current_name});

            stageZoom.initZoom();
        }

        stagePaginate.initPaginate();

        /**
         * Preloader
         */
        this.main.emit('process_end', {}, current_name);
    };

    initLocalizationAndVideo = () => {
        this.defaultPaginateInit(() => {
            const {btn_video} = this.get();

            const execute = (event, options) => {
                let occ_button = this.getObject(options, 'occ_button');
                this.openVideo(occ_button, options);
            };

            this._handleVideo && btn_video.off('mousedown', this._handleVideo);
            this._handleVideo = btn_video.on('mousedown', execute, false, false, {
                occ_button: btn_video,
                name: 'video',
                uri: `${this.$base_uri}/public/media/pd-adelino-grand-dialogo-localizacao-5-1.mp4`
            });
        });
    };

    initVideos = () => {
        this.defaultInit(() => {
            const {
                btn_video01,
                btn_video02,
                btn_video03,
                btn_video04,
                btn_video05,
                btn_video06
            } = this.getFrame();

            const execute = (event, options) => {
                let occ_button = this.getObject(options, 'occ_button');
                this.openVideo(occ_button, options);
            };

            this._handleVideo01 && btn_video01.off('mousedown', this._handleVideo01);
            this._handleVideo01 = btn_video01.on('mousedown', execute, false, false, {
                occ_button: btn_video01,
                name: 'video-01',
                uri: `${this.$base_uri}/public/media/r/frente.mp4`
            });

            this._handleVideo02 && btn_video02.off('mousedown', this._handleVideo02);
            this._handleVideo02 = btn_video02.on('mousedown', execute, false, false, {
                occ_button: btn_video02,
                name: 'video-02',
                uri: `${this.$base_uri}/public/media/r/fundo.mp4`
            });

            this._handleVideo03 && btn_video03.off('mousedown', this._handleVideo03);
            this._handleVideo03 = btn_video03.on('mousedown', execute, false, false, {
                occ_button: btn_video03,
                name: 'video-03',
                uri: `${this.$base_uri}/public/media/r/lateral.mp4`
            });

            this._handleVideo04 && btn_video04.off('mousedown', this._handleVideo04);
            this._handleVideo04 = btn_video04.on('mousedown', execute, false, false, {
                occ_button: btn_video04,
                name: 'video-04',
                uri: `${this.$base_uri}/public/media/nr/frente.mp4`
            });

            this._handleVideo05 && btn_video05.off('mousedown', this._handleVideo05);
            this._handleVideo05 = btn_video05.on('mousedown', execute, false, false, {
                occ_button: btn_video05,
                name: 'video-05',
                uri: `${this.$base_uri}/public/media/nr/esquerdo.mp4`
            });

            this._handleVideo06 && btn_video06.off('mousedown', this._handleVideo06);
            this._handleVideo06 = btn_video06.on('mousedown', execute, false, false, {
                occ_button: btn_video06,
                name: 'video-06',
                uri: `${this.$base_uri}/public/media/nr/direito.mp4`
            });
        });
    };

    initSingle = () => {
        this.defaultInit(() => {
            const {current_name} = this.props;
            this.main.$stageMemory({current_name: current_name}).initMemory();
        });
    };

    initNews = () => {
        this.defaultInit(() => {
            let {occ_marquee, occ_o_clock, occ_news} = this.getFrame();

            let {properties} = this.getComposition('marquee').getLibrary();

            occ_marquee.x = 1920;

            occ_marquee.y = occ_marquee.y + 10;

            occ_o_clock.y = occ_o_clock.y + 12;
            occ_o_clock.x = occ_o_clock.x + 10;

            this.getOClockMoment('get-o-clock-moment', (time) => {
                occ_o_clock.text = time;
            });

            let data = [
                {
                    text: 'Mercado imobiliário paga o dobro do CDI para quem investe no projeto – não no imóvel.',
                    uri: 'http://latest-web.local/public/images/news/josue-isai-ramos-figueroa-qvBYnMuNJ9A-unsplash.jpg'
                },
                {
                    text: 'Investimento imobiliário: há luz no fim do túnel.',
                    uri: 'http://latest-web.local/public/images/news/shutterstock_258100103.jpg'
                },
                {
                    text: 'Por que ser corretor de imóveis atrai cada vez mais brasileiros?',
                    uri: 'http://latest-web.local/public/images/news/shutterstock_349275089.jpg'
                },
                {
                    text: 'Alta do aluguel residencial desacelera para 0,33% em setembro, diz FGV.',
                    uri: 'http://latest-web.local/public/images/news/predios3.174301.jpg'
                },
                {
                    text: 'O que levou à crise no mercado de casas de luxo em Londres.',
                    uri: 'http://latest-web.local/public/images/news/416964157-red.jpg'
                },
                {
                    text: 'A construtora de casas de luxo que vai se aventurar em data centers na Índia.',
                    uri: 'http://latest-web.local/public/images/news/416066978-1-1.jpg'
                },
                {
                    text: 'Aluguel residencial sobe 1,93% em agosto, após queda em julho, afirma FGV.',
                    uri: 'http://latest-web.local/public/images/news/real-estate-6688945_640.jpg'
                },
                {
                    text: 'Imóveis no Rio de Janeiro estão mais baratos em 2024, mostra o FipeZAP.',
                    uri: 'http://latest-web.local/public/images/news/1024px-Leblon_rio.jpg'
                },
                {
                    text: 'Bancos e fintechs apostam em financiamento com garantia em imóvel já alienado.',
                    uri: 'http://latest-web.local/public/images/news/prediosemsp.jpg'
                },
                {
                    text: 'Mercado imobiliário ainda dá sinais de aquecimento, apesar de preços elevados.',
                    uri: 'http://latest-web.local/public/images/news/real-estate-6688945_1280.jpg'
                }
            ];

            let bitmap = null;

            const initMarquee = () => {
                let {text, uri} = this.randomArray(data);

                bitmap && occ_news.occ_gallery.occ_effect.occ_image.removeChild(bitmap);

                bitmap = (new createjs.Bitmap(uri));

                bitmap.image.onload = () => {

                    let
                        _scale = 1,
                        scaleX = (bitmap.scaleX * this.toFloat(_scale)),
                        scaleY = (bitmap.scaleY * this.toFloat(_scale)),
                        boundsImg = bitmap.getBounds(),
                        big_width = this.calcSize(1, scaleX, boundsImg.width, true),
                        big_height = this.calcSize(1, scaleY, boundsImg.height, true);


                    bitmap.scaleX = scaleX;
                    bitmap.scaleY = scaleY;

                    occ_news.occ_gallery.occ_effect.occ_image.x = this.bigToNumber((new Big(properties.width)).minus(big_width).div(this.$controller.big_2), false, 'up');
                    occ_news.occ_gallery.occ_effect.occ_image.y = this.bigToNumber((new Big(properties.height)).minus(big_height).div(this.$controller.big_2), false, 'up');

                    occ_news.occ_gallery.occ_effect.occ_image.addChild(bitmap);

                    occ_marquee.text = text;

                    let bounds = occ_marquee.getBounds();

                    const tween = createjs.Tween.get(occ_marquee, {loop: true, override: true});

                    occ_news.occ_gallery.gotoAndPlay('start');

                    tween.to({x: -Math.round(bounds.width)}, bounds.width * 6).call((object) => {
                        object.paused = true;

                        occ_news.occ_gallery.gotoAndPlay('complete');

                        let {text} = this.randomArray(data);

                        occ_marquee.text = text;

                        //occ_news.occ_gallery.occ_effect;

                        setTimeout(initMarquee, 1000);


                    });
                };


            };

            initMarquee();

            //
            // setTimeout(() => {
            //     createjs.Tween.removeTweens(marquee);
            // }, 10000);
        });
    };

    /**
     * Single page.
     * Included stageSun and helpersIcon color.
     */
    init360 = () => {
        this.defaultInit(() => {
            const
                _this_frame = this.getFrame(),
                {current_name} = this.props;

            this.$helpersIcon.setIcon(_this_frame, button.color_icon);

            this.main.$stage360({current_name: current_name}).init360();
        });
    };

    // initLocalization = () => {
    //     const
    //         {current_name} = this.props,
    //         _this_current = this.get(),
    //         stagePaginate = this.main.$stagePaginate({current_name: current_name}),
    //         occ_pseudo_frame = this.verifyField(_this_current, 'occ_pseudo_frame01'),
    //         btn_frame = this.verifyField(_this_current, 'btn_frame01'),
    //         children = this.verifyField(_this_current, 'occ_frame_03.occ_external.children');
    //
    //     this.main.$current_name = current_name;
    //
    //     this.gotoAndStopFrame(_this_current, frame);
    //
    //     this.pageIn();
    //
    //     this.$helpersIcon.disabledAnimate(_this_current)(1, 'frame');
    //
    //     this.$helpersIcon.setIcon(_this_current, button.color_icon, 1);
    //
    //     this.$helpersIcon.setIcon(_this_current, button.color_icon, 2);
    //
    //     this.$helpersIcon.setIcon(_this_current, button.color_icon, 3);
    //
    //     occ_pseudo_frame && (occ_pseudo_frame.visible = true);
    //
    //     btn_frame && (btn_frame.visible = false);
    //
    //     stagePaginate.initPaginate(true);
    //
    //     let external = {
    //         '01': {
    //             name: 'ALLTAP',
    //             uri: 'https://www.alltap.com.br'
    //         },
    //         '02': {
    //             name: 'ANIMATE CSS',
    //             uri: 'https://animate.style/'
    //         },
    //         '03': {
    //             name: 'BOOTSTRAP 5',
    //             uri: 'https://getbootstrap.com/docs/5.0/getting-started/introduction'
    //         },
    //         '04': {
    //             name: 'REACT JS',
    //             uri: 'https://pt-br.legacy.reactjs.org'
    //         },
    //         '05': {
    //             name: 'A-FRAME',
    //             uri: 'https://aframe.io'
    //         },
    //         '06': {
    //             name: 'CREATEJS',
    //             uri: 'https://createjs.com'
    //         },
    //         '07': {
    //             name: 'ADOBE ANIMATE',
    //             uri: 'https://www.adobe.com/br/products/animate.html'
    //         }
    //     }
    //
    //     if (this.isArrayIf(children)) {
    //         for (let property of children) {
    //             let
    //                 name = property.name,
    //                 number = this.toNumber(name),
    //                 occ = this.padWithLeadingZeros(number, 2);
    //
    //             this.openExternal(property, external[occ]);
    //         }
    //     }
    //
    //     /**
    //      * Preloader
    //      */
    //     this.main.emit('process_end', {}, current_name);
    // };
}

/**
 *
 * @type {{}}
 */
PageCurrent.propTypes = {
    current_name: PropTypes.string
};

/**
 *
 * @type {{}}
 */
PageCurrent.defaultProps = {
    current_name: null
};

/**
 *
 * @param ClassObject
 * @param className {className: 'className'}
 * @returns {function(*): function({}=): *} pass _this ClassMain (required) and props {} (optional)
 */
export default WithType(PageCurrent, {className: 'PageCurrent'});
